import { Effect } from "postprocessing";
import * as THREE from "three";
import ditheringShader from "./DitheringShader.ts";
import { Uniform, Color } from "three";

/**
 * Interface for dithering effect options
 */
export interface DitheringEffectOptions {
  time?: number;
  resolution?: THREE.Vector2;
  gridSize?: number;
  luminanceMethod?: number;
  invertColor?: boolean;
  pixelSizeRatio?: number;
  grayscaleOnly?: boolean;
  tintColor?: string;
}

/**
 * Implementation of the dithering effect
 * Applies a dithering pattern to the rendered scene
 */
export class DitheringEffect extends Effect {
  /**
   * Map of uniforms used by the shader
   */
  uniforms: Map<string, THREE.Uniform<number | THREE.Vector2 | THREE.Color>>; // ✅ düzeltildi

  constructor({
    time = 0,
    resolution = new THREE.Vector2(1, 1),
    gridSize = 4.0,
    luminanceMethod = 0,
    invertColor = false,
    pixelSizeRatio = 1,
    grayscaleOnly = false,
    tintColor = "",
  }: DitheringEffectOptions = {}) {
    const uniforms = new Map<
      string,
      THREE.Uniform<number | THREE.Vector2 | THREE.Color>
    >([
      ["time", new THREE.Uniform(time)],
      ["resolution", new THREE.Uniform(resolution)],
      ["gridSize", new THREE.Uniform(gridSize)],
      ["luminanceMethod", new THREE.Uniform(luminanceMethod)],
      ["invertColor", new THREE.Uniform(invertColor ? 1 : 0)],
      ["ditheringEnabled", new THREE.Uniform(1)],
      ["pixelSizeRatio", new THREE.Uniform(pixelSizeRatio)],
      ["grayscaleOnly", new THREE.Uniform(grayscaleOnly ? 1 : 0)],
      ["tintColor", new Uniform(new Color(tintColor))], // ✅ Color tipi geldiği için hata bitti
    ]);

    super("DitheringEffect", ditheringShader, { uniforms });
    this.uniforms = uniforms;
  }

  update(
    renderer: THREE.WebGLRenderer,
    inputBuffer: THREE.WebGLRenderTarget,
    deltaTime: number
  ): void {
    const timeUniform = this.uniforms.get("time");
    if (timeUniform && typeof timeUniform.value === "number") {
      timeUniform.value += deltaTime;
    }

    const resolutionUniform = this.uniforms.get("resolution");
    if (resolutionUniform?.value instanceof THREE.Vector2) {
      resolutionUniform.value.set(inputBuffer.width, inputBuffer.height);
    }
  }

  setGridSize(size: number): void {
    const gridSizeUniform = this.uniforms.get("gridSize");
    if (gridSizeUniform) gridSizeUniform.value = size;
  }

  setPixelSizeRatio(ratio: number): void {
    const pixelSizeRatioUniform = this.uniforms.get("pixelSizeRatio");
    if (pixelSizeRatioUniform) pixelSizeRatioUniform.value = ratio;
  }

  setGrayscaleOnly(grayscaleOnly: boolean): void {
    const grayscaleOnlyUniform = this.uniforms.get("grayscaleOnly");
    if (grayscaleOnlyUniform)
      grayscaleOnlyUniform.value = grayscaleOnly ? 1 : 0;
  }
}
